import React, { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Star } from "lucide-react";
import { AirportSearch } from "@/components/AirportSearch";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useInputValidation } from "@/hooks/use-input-validation";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Input } from "@/components/ui/input";

export interface ReviewFormData {
  airport_name: string;
  airport_location: string;
  review_text: string;
  film_friendly_score: number;
  hand_check_success: boolean;
  latitude: string;
  longitude: string;
  terminal: string;
  hand_check_date?: string;
}

interface Airport {
  id: number;
  name: string;
  iata_code: string | null;
  location: string;
}

interface ReviewFormProps {
  formData: ReviewFormData;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  onAirportSelect: (airport: Airport) => void;
  onFormDataChange: (newData: Partial<ReviewFormData>) => void;
}

export function ReviewForm({
  formData,
  isSubmitting,
  onSubmit,
  onCancel,
  onAirportSelect,
  onFormDataChange
}: ReviewFormProps) {
  const { t } = useTranslation();
  const reviewText = useInputValidation({
    maxLength: 200,
    custom: (value) => {
      const harmfulPatterns = [
        /<script/i,
        /javascript:/i,
        /data:/i,
        /vbscript:/i,
        /onclick/i,
        /onload/i,
        /onerror/i
      ];
      return !harmfulPatterns.some(pattern => pattern.test(value));
    }
  });

  const terminalValidation = useInputValidation({
    maxLength: 1,
    custom: (value) => {
      if (value === '') return true;
      if (/^[A-Za-z]$/.test(value)) return true;
      return /^[1-9]$/.test(value);
    }
  });

  useEffect(() => {
    if (formData.review_text) {
      reviewText.handleChange(formData.review_text);
    }
    if (formData.terminal) {
      terminalValidation.handleChange(formData.terminal);
    }
  }, []);

  useEffect(() => {
    onFormDataChange({ review_text: reviewText.value });
  }, [reviewText.value]);

  useEffect(() => {
    onFormDataChange({ terminal: terminalValidation.value });
  }, [terminalValidation.value]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.airport_name || !formData.airport_location) {
      toast.error("Please select an airport before submitting the review");
      return;
    }

    if (reviewText.error) {
      toast.error(reviewText.error.message);
      return;
    }

    if (terminalValidation.error) {
      toast.error("Terminal must be a single letter (A-Z) or number (1-9)");
      return;
    }

    await onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full">
      <div className="space-y-2">
        <Label>Airport</Label>
        <AirportSearch 
          onSelect={onAirportSelect} 
          selectedAirport={
            formData.airport_name ? {
              name: formData.airport_name,
              location: formData.airport_location
            } : undefined
          }
        />
      </div>

      <div className="space-y-2">
        <Label>Film Friendly Score</Label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(score => (
            <Toggle
              key={score}
              pressed={formData.film_friendly_score >= score}
              onPressedChange={() => onFormDataChange({ film_friendly_score: score })}
              className="p-2 data-[state=on]:text-yellow-400"
            >
              <Star
                className="h-5 w-5"
                fill={formData.film_friendly_score >= score ? "currentColor" : "none"}
              />
            </Toggle>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Hand Check Available</Label>
        <ToggleGroup
          type="single"
          value={formData.hand_check_success ? "yes" : "no"}
          defaultValue="yes"
          onValueChange={value => {
            if (value) {
              onFormDataChange({
                hand_check_success: value === "yes"
              });
            }
          }}
          className="justify-start"
        >
          <ToggleGroupItem
            value="yes"
            aria-label="Yes"
            className="data-[state=on]:bg-green-500 data-[state=on]:text-white"
          >
            Yes
          </ToggleGroupItem>
          <ToggleGroupItem
            value="no"
            aria-label="No"
            className="data-[state=on]:bg-red-500 data-[state=on]:text-white"
          >
            No
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="space-y-2">
        <Label>Hand Check Date (Optional)</Label>
        <Input 
          type="date"
          value={formData.hand_check_date || ''}
          onChange={(e) => onFormDataChange({ hand_check_date: e.target.value })}
          max={new Date().toISOString().split('T')[0]}
        />
      </div>

      <div className="space-y-2">
        <Label>Terminal (Optional)</Label>
        <Input 
          value={terminalValidation.value}
          onChange={(e) => terminalValidation.handleChange(e.target.value)}
          onBlur={terminalValidation.handleBlur}
          placeholder="A-Z or 1-9"
          maxLength={1}
          className="w-20"
        />
        {terminalValidation.error && terminalValidation.isTouched && (
          <Alert variant="destructive">
            <AlertDescription>
              Terminal must be a single letter (A-Z) or number (1-9)
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label htmlFor="review_text">Review (Optional)</Label>
          <span className="text-sm text-muted-foreground">
            {reviewText.value.length}/200
          </span>
        </div>
        <Textarea
          id="review_text"
          value={reviewText.value}
          onChange={(e) => reviewText.handleChange(e.target.value)}
          onBlur={reviewText.handleBlur}
          maxLength={200}
          className="min-h-[100px]"
        />
        {reviewText.error && reviewText.isTouched && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>
              {reviewText.error.message}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Button type="submit" disabled={isSubmitting || (reviewText.error && reviewText.isTouched)} className="w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Adding Review...
          </>
        ) : (
          "Add Review"
        )}
      </Button>
    </form>
  );
}
